import apiClient from '../../../../shared/api/apiClient'
import { ENDPOINTS } from '../../../../shared/api/endpoints'

export interface CreateDepartmentDto {
  departmentName: string
  departmentCode: string
  description?: string
}

export interface DepartmentResponseDto {
  id: string
  orgId: string
  branchId: string
  departmentName: string
  departmentCode: string
  description?: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface UpdateDepartmentDto {
  departmentName?: string
  departmentCode?: string
  description?: string
  isActive?: boolean
}

export interface DepartmentListQuery {
  page?: number
  limit?: number
  search?: string
  isActive?: boolean
}

export const MAX_DEPARTMENT_PAGE_SIZE = 100

export interface DepartmentListResponse {
  items: DepartmentResponseDto[]
  total: number
}

export const departmentApi = {
  /**
   * List departments: GET /api/departments?page=1&limit=10&isActive=true
   * Bearer token is automatically attached by apiClient requestInterceptor
   */
  async listDepartments(query?: DepartmentListQuery, branchId?: string): Promise<DepartmentListResponse> {
    const headers: Record<string, string> = {}
    if (branchId && branchId.trim()) {
      headers['x-branch-id'] = branchId.trim()
    }
    const sanitizedQuery = query
      ? {
          ...query,
          limit: query.limit !== undefined ? Math.min(Math.max(query.limit, 1), MAX_DEPARTMENT_PAGE_SIZE) : undefined,
        }
      : undefined

    const response = await apiClient.get<any>(ENDPOINTS.DEPARTMENTS.LIST, {
      params: sanitizedQuery,
      headers,
    })
    const payload = response.data
    const dataObj = payload?.data || payload
    const items: DepartmentResponseDto[] = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.items)
        ? payload.items
        : Array.isArray(dataObj?.items)
          ? dataObj.items
          : Array.isArray(dataObj)
            ? dataObj
            : []
    const meta = payload?.meta || dataObj?.meta
    const total = payload?.total ?? dataObj?.total ?? meta?.total ?? items.length
    return { items, total }
  },

  /**
   * Load ALL departments: requests page 1 with limit=100, then sequentially or concurrently
   * requests any remaining pages if totalPages > 1 or total > items.length, and merges items in order.
   */
  async getAllDepartments(branchId?: string, isActive = true): Promise<DepartmentResponseDto[]> {
    const firstRes = await this.listDepartments({ page: 1, limit: MAX_DEPARTMENT_PAGE_SIZE, isActive }, branchId)
    const allItems = [...firstRes.items]
    const total = firstRes.total
    const totalPages = Math.ceil(total / MAX_DEPARTMENT_PAGE_SIZE)

    if (totalPages > 1) {
      const remainingPages: number[] = []
      for (let p = 2; p <= totalPages; p++) {
        remainingPages.push(p)
      }
      const restResults = await Promise.all(
        remainingPages.map((page) =>
          this.listDepartments({ page, limit: MAX_DEPARTMENT_PAGE_SIZE, isActive }, branchId)
        )
      )
      restResults.forEach((res) => {
        allItems.push(...res.items)
      })
    }

    return allItems
  },

  /**
   * Create a department: POST /api/departments
   */
  async createDepartment(payload: CreateDepartmentDto, branchId?: string): Promise<DepartmentResponseDto> {
    const headers: Record<string, string> = {}
    if (branchId && branchId.trim()) {
      headers['x-branch-id'] = branchId.trim()
    }
    const response = await apiClient.post<any>(ENDPOINTS.DEPARTMENTS.CREATE, payload, { headers })
    const resData = response.data
    return (resData?.data || resData) as DepartmentResponseDto
  },

  /**
   * Get department by id: GET /api/departments/:id
   */
  async getDepartmentById(id: string, branchId?: string): Promise<DepartmentResponseDto> {
    const headers: Record<string, string> = {}
    if (branchId && branchId.trim()) {
      headers['x-branch-id'] = branchId.trim()
    }
    const response = await apiClient.get<any>(ENDPOINTS.DEPARTMENTS.GET_BY_ID(id), { headers })
    const resData = response.data
    return (resData?.data || resData) as DepartmentResponseDto
  },

  /**
   * Update department: PATCH /api/departments/:id
   */
  async updateDepartment(id: string, payload: UpdateDepartmentDto, branchId?: string): Promise<DepartmentResponseDto> {
    const headers: Record<string, string> = {}
    if (branchId && branchId.trim()) {
      headers['x-branch-id'] = branchId.trim()
    }
    const response = await apiClient.patch<any>(ENDPOINTS.DEPARTMENTS.UPDATE(id), payload, { headers })
    const resData = response.data
    return (resData?.data || resData) as DepartmentResponseDto
  },

  /**
   * Delete department: DELETE /api/departments/:id
   */
  async deleteDepartment(id: string, branchId?: string): Promise<void> {
    const headers: Record<string, string> = {}
    if (branchId && branchId.trim()) {
      headers['x-branch-id'] = branchId.trim()
    }
    await apiClient.delete(ENDPOINTS.DEPARTMENTS.DELETE(id), { headers })
  },

  /**
   * Get department KPIs: GET /api/dashboard/kpis/department
   */
  async getDepartmentKpis(): Promise<any> {
    const response = await apiClient.get<any>(ENDPOINTS.DASHBOARD.KPIS_DEPARTMENT)
    return response.data?.data ?? response.data
  },
}

export const departmentsApi = departmentApi
